import FireResistentGlass from './Glass/FireResistentGlass.js';
import GenericGlass from './Glass/GenericGlass.js';
import FilterHandler from './FilterHandler.js';
import GlassStructure from './Glass/GlassStructure.js';
import UIFireResistant from './glassUI/UIFireResistant.js';
import UIMiddleGlass from './glassUI/UIMiddleGlass.js';
import UIOutsideGlass from './glassUI/UIOutsideGlass.js';
import SoundUIElement from './elementsUI/SoundUIElement.js';

import Papa from 'papaparse';

const API_URL = 'https://cdr-admin.bitcreed.ch/wp-json/wp/v2/configurator-data';

export default class UIHandler
{
  constructor(filterHandler, webglComponent, onLoad)
  {
    this.soundUIElement = new SoundUIElement(this, '.sound');
    this.structure = new GlassStructure(webglComponent, this.soundUIElement, filterHandler.applicationFilter);

    filterHandler.uiHandler = this;

    this.filterHandler = filterHandler;
    this.webglComponent = webglComponent;

    this.uIFireResistant = new UIFireResistant(this.structure, '.fire-resistant', this.filterHandler, this);
    this.uIMiddleGlass = new UIMiddleGlass(this.structure, '.middle-glass', this.filterHandler, this);
    this.uIOutsideGlass = new UIOutsideGlass(this.structure, '.outside-glass', this.filterHandler, this);

    this.ui_warning = document.querySelector('.warning');

    // API data storage
    this.apiData = null;
    this.pricing = null;
    this.translations = null;
    this.maxDimensions = null;

    // Load data from API, fall back to static CSVs
    this._loadFromAPI(onLoad);
  }

  async _loadFromAPI(onLoad)
  {
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const data = await res.json();

      // Check maintenance mode
      if (data.maintenance_settings?.maintenance_mode?.configurator_enabled === false) {
        this._showMaintenanceOverlay(data.maintenance_settings.maintenance_mode);
        return;
      }

      // Store API data for pricing, sound, datasheets
      this.apiData = data;
      window._cdrApiData = data;

      // Store pricing config
      this.pricing = data.pricing || null;
      window._cdrPricing = this.pricing;

      // Store volume price tables
      this.volumePriceTables = data.volume_price_tables || [];
      window._cdrVolumePriceTables = this.volumePriceTables;

      // Store translations
      try {
        this.translations = JSON.parse(data.glassdb?.glass_translation_json || '[]');
      } catch (e) {
        this.translations = [];
      }

      // Store max dimensions
      this.maxDimensions = {
        width: parseFloat(data.glassdb?.max_glass_width) || 3060,
        height: parseFloat(data.glassdb?.max_glass_height) || 2085
      };
      window._cdrMaxDimensions = this.maxDimensions;

      // Store sound dampening data
      try {
        this.soundData = JSON.parse(data.sound_dampening?.sound_dampening_table || '{}');
      } catch (e) {
        this.soundData = {};
      }
      window._cdrSoundData = this.soundData;

      // Store generic FRG mappings
      try {
        this.genericFrgs = JSON.parse(data.generic_frgs_json || '{}');
      } catch (e) {
        this.genericFrgs = {};
      }

      // Store datasheets
      this.datasheets = data.datasheets || {};
      window._cdrDatasheets = this.datasheets;

      // Parse CSV data from API
      this._parseAPICSV(data.glassdb);

      console.log('Data loaded from bitcreed API');
      onLoad();
      this.filterHandler.takeSnapshot();

    } catch (e) {
      console.warn('API unavailable, falling back to static CSVs:', e.message);
      this._loadStaticCSV(onLoad);
    }
  }

  _parseAPICSV(glassdb)
  {
    // Parse FRG CSV (with headers)
    // API columns: ID, GLASS_DESCRIPTION, ID_VERAS, FIRE_RESISTANCE_CLASS, THICKNESS,
    //   CLEAR_GLASS, UV_PROTECTION, APPLICATION, STRUCTURE, MANUFACTURER,
    //   U_VALUE, SOUND_INSULATION, M2_WEIGHT, BUTT_JOINTS, MITER,
    //   RESISTANCE_CLASS, MAX_WIDTH, MAX_HEIGHT, LAYERS
    // Constructor expects: id, glasstype, id_app, id_veras, klasse, thickness,
    //   white_glass, uv_protection, application, structure, manufacturer,
    //   u_value, sound, weight, butt_joints, miter, resistance,
    //   max_width, max_height, order, layers
    const vkfMap = {};
    const frgResult = Papa.parse(glassdb.frg_csv, { header: true, skipEmptyLines: true });
    frgResult.data.forEach(row => {
      if (!row.ID) return; // skip empty rows
      const mapped = [
        row.ID, row.GLASS_DESCRIPTION, '', row.ID_VERAS,
        row.FIRE_RESISTANCE_CLASS, row.THICKNESS, row.CLEAR_GLASS,
        row.UV_PROTECTION, row.APPLICATION, row.STRUCTURE,
        row.MANUFACTURER, row.U_VALUE, row.SOUND_INSULATION,
        row.M2_WEIGHT, row.BUTT_JOINTS, row.MITER,
        row.RESISTANCE_CLASS, row.MAX_WIDTH, row.MAX_HEIGHT,
        '', row.LAYERS
      ];
      this.uIFireResistant.add(mapped);

      // Build VKF lookup map
      if (row.ID_VERAS) {
        vkfMap[row.ID_VERAS.trim()] = {
          id: row.ID,
          name: row.GLASS_DESCRIPTION,
          veras: row.ID_VERAS.trim(),
          class: row.FIRE_RESISTANCE_CLASS,
          application: row.APPLICATION,
          structure: row.STRUCTURE,
          manufacturer: row.MANUFACTURER,
          resistance: row.RESISTANCE_CLASS,
          whiteGlass: row.CLEAR_GLASS,
          thickness: row.THICKNESS,
        };
      }
    });
    window._cdrVkfMap = vkfMap;

    // Parse MID CSV (with headers)
    // API columns: ID, GLASS_DESCRIPTION, ID_VERAS, THICKNESS, CLEAR_GLASS,
    //   SOUND_INSULATION, M2_WEIGHT, RESISTANCE_CLASS, UV_PROTECTION,
    //   APPLICATION, MAX_WIDTH, MAX_HEIGHT, LAYERS
    // GenericGlass constructor: id, glasstype, id_app, id_veras, thickness,
    //   white_glass, sound, weight, resistance, uv_protection, application, orders, layers
    const midResult = Papa.parse(glassdb.mid_csv, { header: true, skipEmptyLines: true });
    midResult.data.forEach(row => {
      if (!row.ID) return;
      const mapped = [
        row.ID, row.GLASS_DESCRIPTION, '', row.ID_VERAS,
        row.THICKNESS, row.CLEAR_GLASS, row.SOUND_INSULATION,
        row.M2_WEIGHT, row.RESISTANCE_CLASS, row.UV_PROTECTION,
        row.APPLICATION, '', row.LAYERS
      ];
      this.uIMiddleGlass.add(mapped);
    });

    // Parse OUT CSV (same format as MID)
    const outResult = Papa.parse(glassdb.out_csv, { header: true, skipEmptyLines: true });
    outResult.data.forEach(row => {
      if (!row.ID) return;
      const mapped = [
        row.ID, row.GLASS_DESCRIPTION, '', row.ID_VERAS,
        row.THICKNESS, row.CLEAR_GLASS, row.SOUND_INSULATION,
        row.M2_WEIGHT, row.RESISTANCE_CLASS, row.UV_PROTECTION,
        row.APPLICATION, '', row.LAYERS
      ];
      this.uIOutsideGlass.add(mapped);
    });
  }

  _loadStaticCSV(onLoad)
  {
    const context = this;
    let onLoadCounter = 3;
    let onLoadCallback = () =>
    {
      onLoadCounter--;
      if (onLoadCounter == 0)
      {
        onLoad();
        this.filterHandler.takeSnapshot();
      }
    };

    Papa.parse('./data/frg.csv', {
      download: true,
      step: function(row) { context.uIFireResistant.add(row.data); },
      complete: function() { console.log('frg All done!'); onLoadCallback(); }
    });

    Papa.parse('./data/Outside-Glass.csv', {
      download: true,
      step: function(row) { context.uIOutsideGlass.add(row.data); },
      complete: function() { console.log('og All done!'); onLoadCallback(); }
    });

    Papa.parse('./data/Middle-Glass.csv', {
      download: true,
      step: function(row) { context.uIMiddleGlass.add(row.data); },
      complete: function() { console.log('mg All done!'); onLoadCallback(); }
    });
  }

  _showMaintenanceOverlay(settings)
  {
    const msg = settings.maintenance_mode_text?.maintenance_text_de || 'Der Konfigurator ist derzeit nicht verfügbar.';
    const overlay = document.createElement('div');
    overlay.className = 'maintenance-overlay';
    overlay.innerHTML = `
      <div class="maintenance-card">
        <i class="fas fa-tools" style="font-size:2rem;margin-bottom:1rem;opacity:0.6;"></i>
        <h2>Wartungsmodus</h2>
        <p>${msg}</p>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  select_dropdown_option_mg(selected)
  {
    this.uIMiddleGlass.set(selected, true);
  }

  select_dropdown_option_og(selected)
  {
    this.uIOutsideGlass.set(selected, true);
  }

  select_dropdown_option_frg(selected)
  {
    this.uIFireResistant.set(selected, true);
  }

  set_s1_s2(s1, s2, g1, g2)
  {
    this.structure.setSpacers(s1, s2, g1, g2);
  }

  set_uv(uv)
  {
    this.structure.setUV(uv);
  }

  set_warning()
  {
    this.ui_warning.classList.remove('hidden');
  }

  run_filter()
  {
    this.ui_warning.classList.add('hidden');

    this.uIFireResistant.clear();
    this.uIMiddleGlass.clear();
    this.uIOutsideGlass.clear();

    this.filterHandler.run(
      this.uIFireResistant.get_objects(),
      this.uIMiddleGlass.get_objects(),
      this.uIOutsideGlass.get_objects()
    );

    this.uIFireResistant.recreate(this.set_warning.bind(this));
    this.uIMiddleGlass.recreate(this.set_warning.bind(this));
    this.uIOutsideGlass.recreate(this.set_warning.bind(this));

    // If FRG dropdown is empty after filtering, revert white glass toggle
    if (!this.uIFireResistant.set(1)) {
      const whiteGlassCheckbox = document.querySelector('.white-glass');
      if (whiteGlassCheckbox && whiteGlassCheckbox.checked) {
        whiteGlassCheckbox.checked = false;
        whiteGlassCheckbox.dispatchEvent(new Event('change'));
        return;
      }
    }

    this.filterHandler.callback();
  }
}
